import React, { useState } from 'react';
import { ActivityIcon, CheckCircleIcon, UsersIcon, BanknoteIcon, SettingsIcon, ListIcon, CheckCircle2Icon, AlertCircleIcon, ClockIcon } from 'lucide-react';
import { formatZAR } from '../hooks/useMavisData';
const partners = [{
  id: 'easypay',
  name: 'EasyPay',
  logo: 'E',
  color: 'bg-blue-600',
  borderColor: 'border-l-blue-600',
  type: 'Retail Payment Network',
  description: 'Point-of-sale payment processing for retail vendor locations across South Africa.',
  status: 'Connected',
  transactionsToday: 2145,
  successRate: 99.5,
  volumeToday: 542300,
  avgResponse: '1.2s',
  lastTransaction: '2 mins ago',
  methods: ['Cash', 'Card', 'QR Code']
}, {
  id: 'payat',
  name: 'Pay@',
  logo: 'P@',
  color: 'bg-emerald-600',
  borderColor: 'border-l-emerald-600',
  type: 'Bill Payment Platform',
  description: 'Enables electricity token purchases at retail outlets, ATMs, and online banking platforms.',
  status: 'Connected',
  transactionsToday: 1890,
  successRate: 99.1,
  volumeToday: 478500,
  avgResponse: '0.8s',
  lastTransaction: '5 mins ago',
  methods: ['Cash', 'EFT', 'Mobile']
}, {
  id: 'ozow',
  name: 'Ozow Payments',
  logo: 'O',
  color: 'bg-purple-600',
  borderColor: 'border-l-purple-600',
  type: 'Instant EFT Gateway',
  description: 'Real-time bank-to-bank payments for online and mobile prepaid electricity purchases.',
  status: 'Connected',
  transactionsToday: 857,
  successRate: 98.8,
  volumeToday: 225000,
  avgResponse: '2.1s',
  lastTransaction: '12 mins ago',
  methods: ['Instant EFT', 'Bank Transfer']
}];
const recentTransactions = [{
  id: 'TXN-98234',
  time: '1 min ago',
  partner: 'EasyPay',
  amount: 150,
  meterNumber: '04-1234-5678-90',
  status: 'Success',
  method: 'Card'
}, {
  id: 'TXN-98233',
  time: '3 mins ago',
  partner: 'Pay@',
  amount: 50,
  meterNumber: '04-9876-5432-10',
  status: 'Success',
  method: 'Mobile'
}, {
  id: 'TXN-98232',
  time: '5 mins ago',
  partner: 'Ozow Payments',
  amount: 500,
  meterNumber: '04-4567-8901-23',
  status: 'Pending',
  method: 'Instant EFT'
}, {
  id: 'TXN-98231',
  time: '8 mins ago',
  partner: 'EasyPay',
  amount: 200,
  meterNumber: '04-2345-6789-01',
  status: 'Success',
  method: 'Cash'
}, {
  id: 'TXN-98230',
  time: '12 mins ago',
  partner: 'EasyPay',
  amount: 100,
  meterNumber: '04-3456-7890-12',
  status: 'Failed',
  method: 'Card'
}, {
  id: 'TXN-98229',
  time: '15 mins ago',
  partner: 'Pay@',
  amount: 300,
  meterNumber: '04-5678-9012-34',
  status: 'Success',
  method: 'EFT'
}, {
  id: 'TXN-98228',
  time: '18 mins ago',
  partner: 'Ozow Payments',
  amount: 150,
  meterNumber: '04-6789-0123-45',
  status: 'Success',
  method: 'Instant EFT'
}, {
  id: 'TXN-98227',
  time: '22 mins ago',
  partner: 'EasyPay',
  amount: 50,
  meterNumber: '04-7890-1234-56',
  status: 'Success',
  method: 'QR Code'
}, {
  id: 'TXN-98226',
  time: '25 mins ago',
  partner: 'Pay@',
  amount: 250,
  meterNumber: '04-8901-2345-67',
  status: 'Success',
  method: 'Cash'
}, {
  id: 'TXN-98225',
  time: '30 mins ago',
  partner: 'Ozow Payments',
  amount: 100,
  meterNumber: '04-9012-3456-78',
  status: 'Success',
  method: 'Bank Transfer'
}];
export function PaymentIntegrations() {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Success':
        return 'bg-green-100 text-green-800';
      case 'Pending':
        return 'bg-orange-100 text-orange-800';
      case 'Failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="p-8 max-w-7xl mx-auto animate-in fade-in duration-500" data-id="element-1742">
      {/* Header */}
      <div className="mb-8" data-id="element-1743">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1744">
          Payment Integration Partners
        </h1>
        <p className="text-gray-500" data-id="element-1745">
          Manage payment gateway integrations for prepaid electricity vending.
        </p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-1746">
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-1747">
          <div className="p-3 bg-mavis-teal/10 text-mavis-teal rounded-none" data-id="element-1748">
            <ActivityIcon className="w-5 h-5" data-id="element-1749" />
          </div>
          <div data-id="element-1750">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-1751">
              Total Transactions (Today)
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-1752">4,892</p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-1753">
          <div className="p-3 bg-mavis-green/10 text-mavis-green rounded-none" data-id="element-1754">
            <CheckCircleIcon className="w-5 h-5" data-id="element-1755" />
          </div>
          <div data-id="element-1756">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-1757">
              Success Rate
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-1758">99.2%</p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-1759">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-none" data-id="element-1760">
            <UsersIcon className="w-5 h-5" data-id="element-1761" />
          </div>
          <div data-id="element-1762">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-1763">
              Active Partners
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-1764">3</p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-1765">
          <div className="p-3 bg-mavis-gold/10 text-mavis-gold rounded-none" data-id="element-1766">
            <BanknoteIcon className="w-5 h-5" data-id="element-1767" />
          </div>
          <div data-id="element-1768">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-1769">
              Daily Volume
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-1770">
              {formatZAR(1245800)}
            </p>
          </div>
        </div>
      </div>

      {/* Integration Partner Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" data-id="element-1771">
        {partners.map(partner => <div key={partner.id} className={`bg-white border border-gray-200 border-l-4 ${partner.borderColor} shadow-sm rounded-none flex flex-col`} data-id="element-1772">

            <div className="p-6 flex-1" data-id="element-1773">
              <div className="flex justify-between items-start mb-4" data-id="element-1774">
                <div className="flex items-center gap-3" data-id="element-1775">
                  <div className={`w-12 h-12 ${partner.color} text-white font-bold text-xl flex items-center justify-center rounded-none shadow-sm`} data-id="element-1776">

                    {partner.logo}
                  </div>
                  <div data-id="element-1777">
                    <h3 className="font-bold text-gray-900 text-lg leading-tight" data-id="element-1778">
                      {partner.name}
                    </h3>
                    <p className="text-xs text-gray-500" data-id="element-1779">{partner.type}</p>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-none text-[10px] font-bold uppercase ${partner.status === 'Connected' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1780">

                  {partner.status === 'Connected' ? <CheckCircle2Icon className="w-3 h-3" data-id="element-1781" /> : <AlertCircleIcon className="w-3 h-3" data-id="element-1782" />}
                  {partner.status}
                </span>
              </div>

              <p className="text-sm text-gray-600 mb-6 min-h-[40px]" data-id="element-1783">
                {partner.description}
              </p>

              <div className="grid grid-cols-2 gap-y-4 gap-x-2 mb-6" data-id="element-1784">
                <div data-id="element-1785">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1786">
                    Txns Today
                  </p>
                  <p className="font-mono font-bold text-gray-900" data-id="element-1787">
                    {partner.transactionsToday.toLocaleString()}
                  </p>
                </div>
                <div data-id="element-1788">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1789">
                    Success Rate
                  </p>
                  <p className="font-mono font-bold text-mavis-green" data-id="element-1790">
                    {partner.successRate}%
                  </p>
                </div>
                <div data-id="element-1791">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1792">
                    Volume Today
                  </p>
                  <p className="font-mono font-bold text-gray-900" data-id="element-1793">
                    {formatZAR(partner.volumeToday)}
                  </p>
                </div>
                <div data-id="element-1794">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1795">
                    Avg Response
                  </p>
                  <p className="font-mono font-bold text-gray-900" data-id="element-1796">
                    {partner.avgResponse}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-500 mb-6" data-id="element-1797">
                <ClockIcon className="w-4 h-4" data-id="element-1798" />
                Last transaction: {partner.lastTransaction}
              </div>

              <div data-id="element-1799">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-1800">
                  Supported Methods
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-1801">
                  {partner.methods.map(method => <span key={method} className="inline-flex items-center px-2 py-1 rounded-none text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200" data-id="element-1802">

                      {method}
                    </span>)}
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-100 bg-gray-50 flex gap-3" data-id="element-1803">
              <button className="flex-1 flex items-center justify-center gap-2 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-none hover:bg-gray-50 transition-colors text-sm shadow-sm" data-id="element-1804">
                <SettingsIcon className="w-4 h-4" data-id="element-1805" /> Configure
              </button>
              <button className="flex-1 flex items-center justify-center gap-2 py-2 text-white font-medium rounded-none hover:bg-mavis-teal/90 transition-colors text-sm shadow-sm bg-[#003A4D]" data-id="element-1806">
                <ListIcon className="w-4 h-4" data-id="element-1807" /> Transactions
              </button>
            </div>
          </div>)}
      </div>

      {/* Recent Transactions Table */}
      <div className="bg-white rounded-none shadow-sm border border-gray-200 overflow-hidden" data-id="element-1808">
        <div className="p-5 border-b border-gray-200 bg-gray-50" data-id="element-1809">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-1810">
            Recent Transactions
          </h2>
        </div>
        <div className="overflow-x-auto" data-id="element-1811">
          <table className="w-full text-left border-collapse" data-id="element-1812">
            <thead data-id="element-1813">
              <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider border-b border-gray-200" data-id="element-1814">
                <th className="p-4 font-medium" data-id="element-1815">Time</th>
                <th className="p-4 font-medium" data-id="element-1816">Transaction ID</th>
                <th className="p-4 font-medium" data-id="element-1817">Partner</th>
                <th className="p-4 font-medium" data-id="element-1818">Meter Number</th>
                <th className="p-4 font-medium" data-id="element-1819">Method</th>
                <th className="p-4 font-medium text-right" data-id="element-1820">Amount</th>
                <th className="p-4 font-medium text-center" data-id="element-1821">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-1822">
              {recentTransactions.map(txn => <tr key={txn.id} className="hover:bg-gray-50 transition-colors" data-id="element-1823">
                  <td className="p-4 text-sm text-gray-500" data-id="element-1824">{txn.time}</td>
                  <td className="p-4 font-mono text-sm text-gray-900" data-id="element-1825">
                    {txn.id}
                  </td>
                  <td className="p-4 text-sm font-medium text-gray-900" data-id="element-1826">
                    {txn.partner}
                  </td>
                  <td className="p-4 font-mono text-sm text-gray-600" data-id="element-1827">
                    {txn.meterNumber}
                  </td>
                  <td className="p-4 text-sm text-gray-600" data-id="element-1828">{txn.method}</td>
                  <td className="p-4 text-right font-mono font-bold text-gray-900" data-id="element-1829">
                    {formatZAR(txn.amount)}
                  </td>
                  <td className="p-4 text-center" data-id="element-1830">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-none text-xs font-medium ${getStatusBadge(txn.status)}`} data-id="element-1831">

                      {txn.status}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}